import { useState } from "react";
import { API, axios } from "@/api";
import { Plus, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const OFFER_TYPES = [
  { value: "free_item", label: "Free Item" },
  { value: "free_drink", label: "Free Drink" },
  { value: "free_dessert", label: "Free Dessert" },
  { value: "free_meal", label: "Free Meal" },
  { value: "discount_percent", label: "Discount %" },
  { value: "discount_fixed", label: "Discount $" },
  { value: "points_bonus", label: "Points Bonus" },
  { value: "points_multiplier", label: "Points Multiplier" },
  { value: "experience", label: "Experience" },
  { value: "loyalty_tier_upgrade", label: "Loyalty Upgrade" },
  { value: "custom", label: "Other" },
];

const PROGRAM_TYPES = [
  { value: "automatic", label: "Automatic" },
  { value: "tier_based", label: "Tier Based" },
  { value: "points_based", label: "Points Based" },
  { value: "punch_card", label: "Punch Card" },
  { value: "subscription", label: "Subscription" },
  { value: "app_only", label: "App Only" },
  { value: "email_signup", label: "Email Signup" },
  { value: "partnership", label: "Partnership" },
  { value: "social_media", label: "Social Media" },
  { value: "custom", label: "Other" },
];

const REDEMPTION_METHODS = [
  { value: "mention_at_register", label: "Mention at register" },
  { value: "show_app", label: "Show app" },
  { value: "online_only", label: "Online only" },
  { value: "call_ahead", label: "Call ahead" },
  { value: "automatic", label: "Automatic" },
  { value: "show_id", label: "Show ID" },
  { value: "email_coupon", label: "Email coupon" },
  { value: "custom", label: "Other" },
];

export default function SubmitDealSheet({ businessId, businessName, onClose }) {
  const [form, setForm] = useState({
    offer_title: "",
    offer_type: "free_item",
    offer_program_type: "automatic",
    redemption_method: "mention_at_register",
    requires_signup: false,
    signup_url: "",
    signup_lead_days: "",
    min_spend: 0,
    offer_source_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!form.offer_title) {
      toast.error("Please describe the offer");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("access_token");
      await axios.post(`${API}/birthday-offers/submit`, {
        business_id: businessId,
        ...form,
        signup_lead_days: form.signup_lead_days ? parseInt(form.signup_lead_days) : null,
        min_spend: parseFloat(form.min_spend) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" data-testid="submit-deal-success">
        <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-6 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">Thanks for sharing!</h3>
          <p className="text-sm text-slate-600">Our team will review this and add it to the app soon. Your contribution helps the whole birthday community.</p>
          <button onClick={onClose} className="w-full py-2.5 rounded-xl bg-amber-500 text-white font-bold text-sm" data-testid="submit-deal-done-btn">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center" data-testid="submit-deal-sheet">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Submit a Deal</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-testid="submit-deal-close-btn">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <p className="text-xs text-slate-500">for {businessName}</p>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">What is the offer? *</label>
            <input type="text" value={form.offer_title} onChange={(e) => setForm(f => ({ ...f, offer_title: e.target.value }))}
              placeholder="e.g. Free drink on your birthday" className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
              data-testid="submit-deal-title" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Offer type</label>
              <select value={form.offer_type} onChange={(e) => setForm(f => ({ ...f, offer_type: e.target.value }))}
                className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5" data-testid="submit-deal-type">
                {OFFER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Program type</label>
              <select value={form.offer_program_type} onChange={(e) => setForm(f => ({ ...f, offer_program_type: e.target.value }))}
                className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5" data-testid="submit-deal-program">
                {PROGRAM_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">How to claim</label>
            <select value={form.redemption_method} onChange={(e) => setForm(f => ({ ...f, redemption_method: e.target.value }))}
              className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5" data-testid="submit-deal-redemption">
              {REDEMPTION_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>

          <div className="flex items-center justify-between">
            <label className="text-xs font-semibold text-slate-600">Requires signup?</label>
            <button onClick={() => setForm(f => ({ ...f, requires_signup: !f.requires_signup }))}
              className={`w-10 h-6 rounded-full transition-colors ${form.requires_signup ? "bg-amber-500" : "bg-slate-300"}`}
              data-testid="submit-deal-signup-toggle">
              <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${form.requires_signup ? "translate-x-4" : ""}`} />
            </button>
          </div>

          {form.requires_signup && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 block mb-1">Signup URL</label>
                <input type="text" value={form.signup_url} onChange={(e) => setForm(f => ({ ...f, signup_url: e.target.value }))}
                  placeholder="https://..." className="w-full text-xs border border-slate-300 rounded-xl px-3 py-2" data-testid="submit-deal-signup-url" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Days before bday</label>
                <input type="number" value={form.signup_lead_days} onChange={(e) => setForm(f => ({ ...f, signup_lead_days: e.target.value }))}
                  placeholder="7" className="w-full text-xs border border-slate-300 rounded-xl px-3 py-2" data-testid="submit-deal-lead-days" />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Min purchase ($)</label>
            <input type="number" value={form.min_spend} onChange={(e) => setForm(f => ({ ...f, min_spend: e.target.value }))}
              className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5" data-testid="submit-deal-min-spend" />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Source URL (optional)</label>
            <input type="text" value={form.offer_source_url} onChange={(e) => setForm(f => ({ ...f, offer_source_url: e.target.value }))}
              placeholder="https://..." className="w-full text-sm border border-slate-300 rounded-xl px-3 py-2.5" data-testid="submit-deal-source-url" />
          </div>
        </div>

        <button onClick={handleSubmit} disabled={submitting || !form.offer_title}
          className="w-full py-3 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 disabled:opacity-50 transition-colors"
          data-testid="submit-deal-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Plus className="w-4 h-4 inline mr-2" />}
          Submit Deal
        </button>
      </div>
    </div>
  );
}
